import React, {useCallback, useContext, useMemo, useState} from 'react';
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {Tag, Card, InputNumber, Button} from "antd";
import {AuthContext} from "../../../../shared/contexts/authContext";
import {getItemName} from "../../../../shared/utils/basic";
import {Sorter} from "../../../../shared/components/Sorter";
import {Datatable} from "../../../../shared/components/datatable/Datatable";
import {useItemKrTextData} from "../../../probability/useItemData";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import {internalApi} from "../../../../common/axios";
import toast from "react-hot-toast";

const Character = () => {
    const {playerData, setPlayerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);
    const [edited, setEdited] = useState({});
    const [savingKey, setSavingKey] = useState(null);

    const columnData = useCallback(() => {
        const actors = Object.entries(playerData.playerInfo.inventoryData?.Actors || {});
        return actors.map(([actorKey, actor]) => ({...actor, key: actorKey}));
    }, [playerData.playerInfo.inventoryData?.Actors]);
    const { data: krItemTextData, isLoading, error } = useItemKrTextData();

    const handleFieldChange = (actorKey, field, value) => {
        setEdited(prev => ({
            ...prev,
            [actorKey]: {...prev[actorKey], [field]: value},
        }));
    };

    const handleSaveActor = async (actorKey) => {
        const change = edited[actorKey];
        if (!change) return;

        const actors = playerData.playerInfo.inventoryData?.Actors || {};
        const nextActors = {
            ...actors,
            [actorKey]: {...actors[actorKey], ...change},
        };
        const nextPlayerInfo = {
            ...playerData.playerInfo,
            inventoryData: {...playerData.playerInfo.inventoryData, Actors: nextActors},
        };

        setSavingKey(actorKey);
        try {
            await internalApi.post('/player/playInfo', {
                playInfo: nextPlayerInfo,
                userKey: playerData.userKey,
                syncId: playerData.syncId,
            });
            setPlayerData(prev => ({...prev, playerInfo: nextPlayerInfo}));
            setEdited(prev => {
                const next = {...prev};
                delete next[actorKey];
                return next;
            });
            toast.success('캐릭터 정보가 저장되었습니다.');
        } catch (e) {
            toast.error(e?.response?.data?.message || '캐릭터 정보 저장 실패');
        } finally {
            setSavingKey(null);
        }
    };

    const columns = useMemo(() => [
        {
            title: '스태프 ID',
            dataIndex: 'Index',
            key: 'Index',
            width: 80,
        },
        {
            title: 'Name',
            dataIndex: 'Index',
            width: 80,
            render: (_, {Index}) => getItemName(krItemTextData[currentUser.target], Index, 3),
        },
        {
            title: '등급',
            dataIndex: 'Grade',
            width: 100,
            sorter: (a, b) => Sorter.DEFAULT(a.Grade, b.Grade),
            render: (_, record) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={edited[record.key]?.Grade ?? record.Grade}
                    onChange={(v) => handleFieldChange(record.key, 'Grade', v)}
                    style={{width: 70}}
                />
            ),
        },
        {
            title: '소울 조각',
            dataIndex: 'SoulStone',
            width: 100,
            sorter: (a, b) => Sorter.DEFAULT(a.SoulStone, b.SoulStone),
            render: (_, record) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={edited[record.key]?.SoulStone ?? record.SoulStone}
                    onChange={(v) => handleFieldChange(record.key, 'SoulStone', v)}
                    style={{width: 80}}
                />
            ),
        },
        {
            title: '스토어',
            dataIndex: 'StoreType',
            width: 60,
            sorter: (a, b) => Sorter.DEFAULT(a.StaffLocation, b.StaffLocation),
        },
        {
            title: '배정 위치',
            dataIndex: 'StaffType',
            width: 90,
            sorter: (a, b) => Sorter.DEFAULT(a.StaffType, b.StaffType),
            render: (_, {StaffType}) => {
                let color, text;
                switch (StaffType) {
                    case 1:
                        color = 'red';
                        text = '점장';
                        break;
                    case 2:
                        color = 'green';
                        text = '서빙';
                        break;
                    case 3:
                        color = 'blue';
                        text = '생산';
                        break;
                    default:
                        color = 'lightgray';
                        text = '배치안됨';
                }
                return <Tag color={color}>{text}</Tag>;
            },
            filters: [
                {
                    text: '점장',
                    value: 1,
                },
                {
                    text: '서빙',
                    value: 2,
                },
                {
                    text: '생산',
                    value: 3,
                },
                {
                    text: '배치안됨',
                    value: 0,
                },
            ],
            onFilter: (value, record) => record.appoint === value,
        },
        {
            title: '스킨',
            dataIndex: 'CostumeIndex',
            width: 80,
        },
        {
            title: '피로도',
            dataIndex: 'Fatigue',
            width: 70,
        },
        {
            title: '맥스 피로도',
            dataIndex: 'MaxFatigue',
            width: 70,
        },
        {
            title: '다락방',
            dataIndex: 'IsGarret',
            width: 80,
            render: (_, {IsGarret}) => (
                <Tag color={IsGarret ? 'red' : 'green'}>
                    {IsGarret ? '다락방 안' : '다락방 밖'}
                </Tag>
            ),
        },
        {
            title: '관리',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button
                    size="small"
                    type="primary"
                    disabled={!edited[record.key]}
                    loading={savingKey === record.key}
                    onClick={() => handleSaveActor(record.key)}
                >
                    저장
                </Button>
            ),
        }
    ], [krItemTextData, currentUser, edited, savingKey]);

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    return (
        <Card style={{margin: 8, padding: 8}}>
            <Datatable columns={columns} rows={columnData()}/>
        </Card>
    );
};

export default Character;